/*
  Motion design tokens — single source of truth for the mobile UI/UX initiative
  (HAP-1886, parent HAP-1884 Phase 0.2). Every later motion phase should pull
  durations / easings from here (or the matching CSS vars / Tailwind tokens)
  instead of hard-coding bespoke one-offs.

  Three mirrored surfaces, all the SAME values:
    • JS         — this module (`import { DURATION, EASING } from '.../motion'`)
    • CSS vars   — `:root { --motion-* }` in index.css (for plain CSS / @apply)
    • Tailwind   — `duration-*` / `ease-*` tokens in tailwind.config.js, which
                   reference the CSS vars, so utility classes stay in sync.

  Keep the numbers identical across all three if you ever change them.
*/

// Durations in milliseconds. 200–400ms is the native "feels instant but smooth"
// sweet spot; `fast` for taps/toggles, `base` for the default transition,
// `slow` for larger surfaces (sheets, hero/route changes).
export const DURATION = {
	fast: 200,
	base: 300,
	slow: 400,
}

// Easing curves. `spring` is the standard springy ease-out used for entrances
// and presses; `standard` is a neutral material-style ease for everyday
// property transitions; `decel`/`accel` for one-directional motion.
export const EASING = {
	spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)', // gentle overshoot, the "spring"
	standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
	decel: 'cubic-bezier(0, 0, 0.2, 1)', // ease-out — enter
	accel: 'cubic-bezier(0.4, 0, 1, 1)', // ease-in — exit
}

// Convenience: a ready-made CSS transition value, e.g. style="transition: ...".
export function transition(props = 'all', duration = DURATION.base, easing = EASING.standard) {
	const list = Array.isArray(props) ? props : [props]
	return list.map((p) => `${p} ${duration}ms ${easing}`).join(', ')
}

export default { DURATION, EASING, transition }
